import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { EXTRAS_HIDDEN, HIDDEN_VIEWS } from "./goods";
import { Ellipsis, Grid } from "./icons";
import { customModules, type CmNavEntry } from "../api/portals";
import BrandMark from "./BrandMark";
import { useAuth } from "../auth/AuthContext";
import "./nav.css";
import { byView, canSee, DESTINATIONS, DOCK, GROUPS, PRIMARY, type View } from "./routes";
import { useInventoryMeta } from "./inventory/common";
import { useAiStatus } from "./ai/useAiStatus";
import { t, useLocale } from "../i18n";

/*
 * app.wealthoak.ai's 2026 masthead (ui/Masthead.tsx there) with the CRM's
 * destinations: the brand, the floating nav pill with its sliding indicator,
 * and the "More" sheet holding every screen. On a phone the pill becomes a
 * bottom dock (design2026/wo-shell.css, copied unchanged).
 */

const NAV_STROKE = 1.5;
const SHEET_STROKE = 1.6;
const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function Masthead({ active, activeId, onGo }: { active: View; activeId?: string | null; onGo: (view: View, id?: string | null) => void }) {
  const navRef = useRef<HTMLElement>(null);
  const indRef = useRef<HTMLSpanElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const lastFocus = useRef<HTMLElement | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetShown, setSheetShown] = useState(false);
  const [sheetVisible, setSheetVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { allow } = useAuth();
  useLocale(); // the nav's names follow the language
  const inventoryOn = useInventoryMeta()?.enabled ?? false;
  // A role sees only the destinations it may open (the server refuses the rest anyway), and a switched-off part of the CRM is not shown.
  const aiStatus = useAiStatus();
  const scanOn = Boolean(aiStatus?.available && aiStatus.scan);
  const visible = DESTINATIONS.filter((d) => canSee(d, allow) && (d.feature !== "inventory" || inventoryOn) && (d.feature !== "ai" || scanOn) && !HIDDEN_VIEWS.has(d.id));
  const shown = (v: View) => visible.some((d) => d.id === v);
  // A screen merged into another (routes.ts `under`) is reached from the top of that one, so the sheet lists it only when the role can't open that one.
  const listed = visible.filter((d) => !d.under || !shown(d.under));
  // On a merged screen the nav marks the screen it sits in.
  const home = byView[active].under && shown(byView[active].under) ? byView[active].under : active;
  // Custom modules (server/custom-modules.js): one sheet entry per module this role may view; refreshed when the builder saves.
  const [modules, setModules] = useState<CmNavEntry[]>([]);
  useEffect(() => {
    const load = () => void customModules.nav().then(setModules, () => setModules([]));
    load();
    window.addEventListener("crm:modules-changed", load);
    return () => window.removeEventListener("crm:modules-changed", load);
  }, []);

  const primary = PRIMARY.includes(home);
  const context = primary ? "" : t(byView[home].title);

  const placeIndicator = useCallback(() => {
    const nav = navRef.current;
    const ind = indRef.current;
    if (!nav || !ind) return;
    const cur = nav.querySelector<HTMLElement>(".wo-nav__item.is-active");
    if (!cur || !nav.clientWidth) {
      ind.style.opacity = "0";
      return;
    }
    ind.style.opacity = "1";
    ind.style.width = `${cur.offsetWidth}px`;
    ind.style.transform = `translate3d(${cur.offsetLeft}px,0,0)`;
  }, []);

  useLayoutEffect(placeIndicator, [placeIndicator, home, context]);

  useEffect(() => {
    // Items change width after the first measure (the web font arrives,
    // "More" names the page), so re-place the indicator whenever one does.
    const nav = navRef.current;
    if (!nav) return;
    const ro = new ResizeObserver(() => placeIndicator());
    nav.querySelectorAll(".wo-nav__item").forEach((el) => ro.observe(el));
    window.addEventListener("resize", placeIndicator);
    void document.fonts?.ready.then(placeIndicator);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", placeIndicator);
    };
  }, [placeIndicator]);

  useEffect(() => {
    const sync = () => setScrolled(window.scrollY > 6);
    sync();
    window.addEventListener("scroll", sync, { passive: true });
    return () => window.removeEventListener("scroll", sync);
  }, []);

  const openSheet = useCallback(() => {
    lastFocus.current = document.activeElement as HTMLElement | null;
    setSheetShown(true);
    setSheetOpen(true);
  }, []);

  const closeSheet = useCallback((restore: boolean) => {
    setSheetOpen(false);
    if (restore && lastFocus.current && document.contains(lastFocus.current)) lastFocus.current.focus({ preventScroll: true });
  }, []);

  // Opening: show it, then fade it in on the next frame. Closing: fade out,
  // then take it out of the page.
  useEffect(() => {
    if (sheetOpen) {
      const raf = requestAnimationFrame(() => setSheetVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setSheetVisible(false);
    if (!sheetShown) return;
    if (reducedMotion()) {
      setSheetShown(false);
      return;
    }
    const t = window.setTimeout(() => setSheetShown(false), 180);
    return () => window.clearTimeout(t);
  }, [sheetOpen, sheetShown]);

  useEffect(() => {
    if (!sheetOpen || !sheetShown) return;
    const sheet = sheetRef.current;
    const cur = sheet?.querySelector<HTMLElement>(".wo-sheet__link.is-active") ?? sheet?.querySelector<HTMLElement>(".wo-sheet__link");
    cur?.focus({ preventScroll: true });
  }, [sheetOpen, sheetShown]);

  useEffect(() => {
    if (!sheetOpen) return;
    function onClick(e: MouseEvent) {
      const t = e.target as Node;
      if (!sheetRef.current?.contains(t) && !moreRef.current?.contains(t)) closeSheet(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        closeSheet(true);
      }
    }
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [sheetOpen, closeSheet]);

  // Arrow keys move through the sheet: up/down within the list, left/right
  // between the columns at the same row.
  function onSheetKey(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
    const sheet = sheetRef.current;
    const focused = document.activeElement as HTMLElement | null;
    if (!sheet || !focused || !sheet.contains(focused)) return;
    e.preventDefault();
    const links = Array.from(sheet.querySelectorAll<HTMLElement>(".wo-sheet__link"));
    const i = links.indexOf(focused);
    if (e.key === "ArrowDown") links[Math.min(links.length - 1, i + 1)]?.focus();
    else if (e.key === "ArrowUp") links[Math.max(0, i - 1)]?.focus();
    else {
      const cols = Array.from(sheet.querySelectorAll<HTMLElement>(".wo-sheet__col"));
      const ci = cols.findIndex((c) => c.contains(focused));
      const within = Array.from(cols[ci].querySelectorAll<HTMLElement>(".wo-sheet__link")).indexOf(focused);
      const target = cols[Math.min(cols.length - 1, Math.max(0, ci + (e.key === "ArrowRight" ? 1 : -1)))];
      const targetLinks = Array.from(target.querySelectorAll<HTMLElement>(".wo-sheet__link"));
      targetLinks[Math.min(within, targetLinks.length - 1)]?.focus();
    }
  }

  function go(view: View, id: string | null = null) {
    closeSheet(false);
    onGo(view, id);
  }

  const mastClass = ["wo-mast", scrolled ? "is-scrolled" : "", sheetVisible ? "is-sheet-open" : ""].filter(Boolean).join(" ");

  return (
    <header id="wo-mast" className={mastClass}>
      <div className="wo-mast__bar">
        <button className="wo-brand" type="button" aria-label={t("WealthOak CRM, go to Dashboard")} onClick={() => go("dashboard")}>
          <BrandMark size={32} />
          <span>
            {t("WealthOak")} <em className="crm-brand-tag">CRM</em>
          </span>
        </button>

        <nav className="wo-nav" aria-label={t("Primary")} ref={navRef}>
          <span className="wo-nav__ind" aria-hidden="true" ref={indRef} />
          {PRIMARY.filter(shown).map((id) => {
            const d = byView[id];
            const on = id === home;
            const Icon = d.icon;
            return (
              <button
                key={id}
                className={`wo-nav__item${on ? " is-active" : ""}${DOCK.includes(id) ? "" : " not-dock"}`}
                type="button"
                data-id={id}
                title={t(d.title)}
                aria-current={on ? "page" : undefined}
                onClick={() => go(id)}
              >
                <span className="wo-nav__ico">
                  <Icon className="wo-i wo-ico" size={18} strokeWidth={NAV_STROKE} />
                </span>
                <span className="wo-nav__text">{t(d.title)}</span>
              </button>
            );
          })}
          <button
            ref={moreRef}
            className={`wo-nav__item wo-nav__more${!primary ? " is-active" : ""}`}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={sheetOpen}
            aria-controls="wo-sheet"
            aria-label={t("More")}
            onClick={(e) => {
              e.stopPropagation();
              if (sheetOpen) closeSheet(true);
              else openSheet();
            }}
          >
            <span className="wo-nav__ico">
              <Ellipsis className="wo-i wo-ico" size={18} strokeWidth={NAV_STROKE} />
            </span>
            {/* "More" names the page you are on when it is off the primary bar */}
            <span className={`wo-nav__text${context ? " has-ctx" : ""}`}>
              <span className="wo-more__ctx">{context}</span>
              <span className="wo-more__base">{t("More")}</span>
            </span>
          </button>
        </nav>

        <div className="wo-mast__end" />
      </div>

      <div className="wo-sheet" id="wo-sheet" role="dialog" aria-label={t("All destinations")} hidden={!sheetShown} ref={sheetRef} onKeyDown={onSheetKey}>
        <div className="wo-sheet__grid">
          {GROUPS.filter((g) => listed.some((d) => d.group === g)).map((g) => (
            <section className="wo-sheet__col" key={g}>
              <h2>{t(g)}</h2>
              <ul>
                {listed.filter((d) => d.group === g).map((d) => {
                  const on = d.id === home;
                  const Icon = d.icon;
                  return (
                    <li key={d.id}>
                      <button type="button" className={`wo-sheet__link${on ? " is-active" : ""}`} data-id={d.id} aria-current={on ? "page" : undefined} onClick={() => go(d.id)}>
                        <span className="wo-sheet__ico">
                          <Icon className="wo-i" size={16} strokeWidth={SHEET_STROKE} />
                        </span>
                        <span className="wo-sheet__txt">
                          <strong>{t(d.title)}</strong>
                          <small>{t(d.desc)}</small>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          {modules.length > 0 && !EXTRAS_HIDDEN && (
            <section className="wo-sheet__col">
              <h2>{t("Modules")}</h2>
              <ul>
                {modules.map((m) => {
                  const on = active === "modules" && activeId === m.key;
                  return (
                    <li key={m.key}>
                      <button type="button" className={`wo-sheet__link${on ? " is-active" : ""}`} data-id={`modules:${m.key}`} aria-current={on ? "page" : undefined} onClick={() => go("modules", m.key)}>
                        <span className="wo-sheet__ico">
                          <Grid className="wo-i" size={16} strokeWidth={SHEET_STROKE} />
                        </span>
                        <span className="wo-sheet__txt">
                          <strong>{m.name}</strong>
                          <small>{m.description ?? t("Custom module")}</small>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
        <div className="wo-sheet__foot">
          <span>
            <kbd>Esc</kbd> {t("close")}
          </span>
        </div>
      </div>
    </header>
  );
}
